// The sentences waivers speak, for every host. PLAN-2026-09-24 W5.
//
// ONE COPY OF EACH. These lived in the simulator (`packages/sim/src/waivers.ts`)
// until a real league needed them too, and a second copy in the league store
// would have been two answers to "what does the log say about this add" that
// agree until the day somebody edits one. The simulator and the store both
// call these now, so for the same state they say the same words — which is
// what lets the simulator stand as the store's reference implementation.
//
// FACTS IN, WORDS OUT. Each takes the facts its sentence needs, already
// resolved to names; none reads a league, a roster or a clock of its own.
// Times are told in the league's zone, which the caller hands in.

import {
  RECENT_ADD_HOURS,
  whenInZone,
  type ClaimOutcome,
  type WaiverPrioritySource,
} from '@fsp/domain';
import { ordinal } from './engine';
import type { DropTo, WaiverRules } from './types';

/** One move as the log and the league chat say it. */
export interface MoveFacts {
  kind: 'add' | 'drop' | 'claim' | 'trade' | 'commissioner';
  added: readonly string[];
  dropped: readonly string[];
  faab: number | null;
  reason: string | null;
  /**
   * A trade's draft picks, sent and received, by name as the day said them
   * (PLAN-2026-10-27-the-next-season §2.6 rule 8). Absent: none.
   */
  picksSent?: readonly string[];
  picksGot?: readonly string[];
}

/**
 * "Priya added Tyler Allgeier ($12), dropped Roschon Johnson." `who` is the
 * manager's name as a team reference carries it; `name` resolves a player id.
 */
export function transactionSummary(
  tx: MoveFacts,
  who: string,
  name: (playerId: string) => string,
): string {
  const names = (ids: readonly string[]) => ids.map(name).join(', ');
  const money = tx.faab !== null ? ` ($${tx.faab})` : '';
  if (tx.kind === 'commissioner') return `${who}: ${tx.reason ?? 'commissioner action'}.`;
  if (tx.kind === 'trade') {
    const sent = tx.dropped.filter((id) => !tx.added.includes(id));
    const list = (ids: readonly string[], picks: readonly string[] = []) =>
      [...ids.map(name), ...picks].join(', ') || 'nothing';
    return `Trade: ${who} sends ${list(sent, tx.picksSent)}, gets ${list(tx.added, tx.picksGot)}.`;
  }
  if (tx.added.length && tx.dropped.length)
    return `${who} added ${names(tx.added)}${money}, dropped ${names(tx.dropped)}.`;
  if (tx.added.length) return `${who} added ${names(tx.added)}${money}.`;
  return `${who} dropped ${names(tx.dropped)}.`;
}

/** The chat line an undo posts: "Priya took that back. Roschon Johnson is back on the roster." */
export function undoneSentence(who: string, back: readonly string[]): string {
  return `${who} took that back.${back.length ? ` ${back.join(', ')} is back on the roster.` : ''}`;
}

/** The chat line a run posts: "Waivers ran: 4 of 9 claims went through." */
export function runChatLine(won: number, decided: number): string {
  return `Waivers ran: ${won} of ${decided} claim${decided === 1 ? '' : 's'} went through.`;
}

/** A run's one-line summary in the log: "9 claims · 4 successful". */
export function runSummary(won: number, decided: number): string {
  return `${decided} claim${decided === 1 ? '' : 's'} · ${won} successful`;
}

/**
 * Why my waiver priority is what it is (B-140, B-139): the Waivers header's
 * sentence beside `WaiversView.priority.source`.
 */
export function priorityWhy(input: {
  rules: WaiverRules;
  source: WaiverPrioritySource;
  rank: number;
  of: number;
  /** My record, for the standings' reasons. */
  record: { wins: number; losses: number };
  /** Where I drafted, 1-based, for the draft-order reason. */
  draftedAt: number;
}): string {
  const { rules, source, rank, of } = input;
  if (source !== 'rolling') {
    const record = `${input.record.wins}–${input.record.losses}`;
    const because =
      source === 'draft-order'
        ? ` because you drafted ${ordinal(input.draftedAt)}. Until a game is final, the team that drafted last claims first.`
        : source === 'frozen'
          ? ` because you finished the regular season ${record}. The order holds through the playoffs.`
          : ` because you are ${record}.`;
    return `You are ${ordinal(rank)} of ${of}${because}${rules.system === 'faab' ? ' It only matters on a tied bid.' : ''}`;
  }
  return rules.system === 'rolling'
    ? `You are ${ordinal(rank)}. Winning a claim sends you to ${ordinal(of)}.`
    : `You are ${ordinal(rank)} of ${of}. It only matters on a tied bid, and winning a claim sends you to ${ordinal(of)}.`;
}

/**
 * A pending claim as it stands: the problem, or that it is legal — against the
 * whole budget, because a pending claim holds none back (B-141).
 */
export function claimStanding(
  rules: WaiverRules,
  problem: string | null,
  bid: number | null,
  budgetLeft: number,
): string {
  if (problem) return problem;
  return rules.system === 'faab'
    ? `Legal. $${bid ?? 0} of the $${budgetLeft} left.`
    : 'Legal as placed.';
}

/**
 * "Wednesday 3:00 AM" in the league's zone, read at `now` — with the date when
 * the weekday alone could name the wrong week (`whenInZone`, the one rule).
 */
function longWhen(ms: number, timeZone: string, now: number): string {
  return whenInZone(ms, timeZone, now, 'long');
}

/** "Wed 3:00 AM" in the league's zone, read at `now`, the same way. */
function shortWhen(ms: number, timeZone: string, now: number): string {
  return whenInZone(ms, timeZone, now, 'short');
}

/**
 * The drop sheet's sentence for where the player goes: `dropsTo` in words,
 * read at `now` — a drop between Tuesday and Wednesday's run clears the
 * Wednesday after next, and says the date.
 */
export function dropSentence(name: string, to: DropTo, timeZone: string, now: number): string {
  if (to.kind === 'waivers')
    return `${name} will sit on waivers until ${longWhen(to.clearsAt, timeZone, now)}, where anyone can put in a claim.`;
  return to.because === 'recent-add'
    ? `Added less than ${RECENT_ADD_HOURS} hours ago, so ${name} skips waivers and becomes a free agent right away.`
    : `${name} becomes a free agent right away.`;
}

/**
 * The consequence line under one player in the drop picker, or null when
 * nothing needs saying. `recentAdd` only when the option can be picked: a
 * recent add skipping waivers is said here, before the move, because it is the
 * one destination that differs from the league's usual (B-138).
 */
export function dropConsequence(facts: {
  locked: boolean;
  recentAdd: boolean;
  /** The position, when this is the roster's only player there. */
  onlyAt: string | null;
  /** The starter slot's name, when he is starting this period. */
  startingAt: string | null;
  bye: boolean;
  out: boolean;
}): string | null {
  const recent = facts.recentAdd
    ? `Goes straight to free agency: added less than ${RECENT_ADD_HOURS} hours ago.`
    : null;
  if (facts.locked) return ['Locked: the game started.', recent].filter(Boolean).join(' ');
  const bits: string[] = [];
  if (recent) bits.push(recent);
  if (facts.onlyAt !== null) bits.push(`Your only ${facts.onlyAt}.`);
  if (facts.startingAt !== null) bits.push(`Starting at ${facts.startingAt} this week.`);
  if (facts.bye) bits.push('On a bye this week.');
  if (facts.out) bits.push('Out this week.');
  return bits.length ? bits.join(' ') : null;
}

/**
 * Why a roster that counts right does not fit its lineup (`WaiverContext.fits`),
 * in words fit for a refusal and for the shared log alike.
 */
export function noLineupSpotSentence(name: string): string {
  return `No lineup spot for ${name}: the bench is full and no open starting slot fits.`;
}

/**
 * Why a player cannot be added because he is on waivers: after a drop or the
 * draft, or only because his game has started (B-142), which is said, or a
 * player who was a free agent this morning reads as a mistake. And when his
 * time has come and the run that decides him has not run yet (`clearsAt` at or
 * before `now`, B-149), that — "until Wed 3:00 AM" read at 3:01 would be a
 * time already past.
 */
export function onWaiversSentence(
  name: string,
  clearsAt: number,
  byKickoffOnly: boolean,
  timeZone: string,
  now: number,
): string {
  if (clearsAt <= now)
    return `${name} is on waivers until the run due ${shortWhen(clearsAt, timeZone, now)} has run. Put in a claim instead.`;
  const until = `on waivers until ${shortWhen(clearsAt, timeZone, now)}. Put in a claim instead.`;
  return byKickoffOnly ? `The game has started, so ${name} is ${until}` : `${name} is ${until}`;
}

/**
 * Why a claim cannot be placed in the last days of a season: the run that
 * would decide it comes after the season ends, when moves close and no run
 * happens (B-152). Said on the refusal, the claim sheet and the row alike.
 */
export function noRunLeftSentence(name: string): string {
  return `The season ends before the next waiver run, so ${name} cannot be claimed.`;
}

/**
 * How a claim still pending when the season closed was settled (outcome
 * `season-over`, B-152): in the log beside every other claim's sentence.
 */
export const SEASON_OVER_SENTENCE = 'The season ended before a waiver run could decide this claim.';

/** One of a manager's claims in a run, for the notification that says how it went. */
export interface ClaimLine {
  outcome: ClaimOutcome;
  playerName: string;
  /** The team that won the player, when somebody did. */
  winningTeamName: string | null;
  winningBid: number | null;
  bid: number | null;
}

/** "A", "A and B", "A, B and C". */
function listOf(items: readonly string[]): string {
  if (items.length <= 2) return items.join(' and ');
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}

/**
 * What `waivers.processed` tells one manager about their own claims
 * (NOTIFICATIONS.md §3.5: "You got ‹Name›" / "‹Name› went to ‹Team› (bid ‹x›,
 * yours ‹y›)"). The wins in one line; each player lost to another team with
 * both bids once the run is over (B-136), at most two of them; and the rest
 * counted, so a notification stays a notification and the Waivers screen
 * says the rest.
 */
export function processedOutcome(lines: readonly ClaimLine[], faab: boolean): string {
  const won = lines.filter((l) => l.outcome === 'won').map((l) => l.playerName);
  const lostTo = lines.filter((l) => l.outcome !== 'won' && l.winningTeamName !== null);
  const other = lines.filter((l) => l.outcome !== 'won' && l.winningTeamName === null);
  const parts: string[] = [];
  if (won.length) parts.push(`You got ${listOf(won)}.`);
  for (const l of lostTo.slice(0, 2)) {
    const bids = faab ? ` (bid $${l.winningBid ?? 0}, yours $${l.bid ?? 0})` : '';
    parts.push(`${l.playerName} went to ${l.winningTeamName}${bids}.`);
  }
  const rest = lostTo.length - Math.min(2, lostTo.length) + other.length;
  if (rest > 0)
    parts.push(
      `${rest} ${parts.length ? 'other ' : ''}claim${rest === 1 ? '' : 's'} did not go through.`,
    );
  return parts.join(' ');
}
